import Phaser from 'phaser';

/** Disposition d'une texture extrudée : à passer à `addTilesetImage`. */
export const EXTRUDED_MARGIN = 1;
export const EXTRUDED_SPACING = 2;

/** Image source de chaque texture extrudée, pour la régénérer si la source change. */
const extrudedFrom = new Map<string, unknown>();

/**
 * Copie une texture découpée en grille de tuiles dans une nouvelle texture
 * où chaque tuile est entourée d'un pixel dupliqué de son propre bord.
 *
 * Sans ça, aux zooms non entiers et pendant le suivi de caméra (positions
 * sub-pixel), le GPU échantillonne parfois la tuile voisine de l'atlas en
 * bord de case : des traits de quadrillage apparaissent entre les tuiles.
 * Avec la bordure dupliquée, ce débordement retombe sur la même couleur.
 *
 * La texture produite a une frame numérotée par tuile (comme un
 * spritesheet) et se passe à `addTilesetImage(…, EXTRUDED_MARGIN, EXTRUDED_SPACING)`.
 * Retourne la clé de la texture extrudée (`{srcKey}:extruded`).
 */
export function extrudeTileTexture(
  scene: Phaser.Scene,
  srcKey: string,
  tileWidth: number,
  tileHeight: number,
  margin = 0,
  spacing = 0,
): string {
  const dstKey = `${srcKey}:extruded`;
  const source = scene.textures.get(srcKey).getSourceImage() as HTMLImageElement | HTMLCanvasElement;
  if (scene.textures.exists(dstKey)) {
    if (extrudedFrom.get(dstKey) === source) return dstKey;
    scene.textures.remove(dstKey);
  }

  const cols = Math.max(1, Math.floor((source.width - 2 * margin + spacing) / (tileWidth + spacing)));
  const rows = Math.max(1, Math.floor((source.height - 2 * margin + spacing) / (tileHeight + spacing)));
  const cellW = tileWidth + EXTRUDED_SPACING;
  const cellH = tileHeight + EXTRUDED_SPACING;

  const canvasTexture = scene.textures.createCanvas(dstKey, cols * cellW, rows * cellH);
  if (!canvasTexture) return srcKey;
  const ctx = canvasTexture.getContext();
  ctx.imageSmoothingEnabled = false;

  const w = tileWidth;
  const h = tileHeight;
  let index = 0;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const sx = margin + col * (tileWidth + spacing);
      const sy = margin + row * (tileHeight + spacing);
      const dx = col * cellW + EXTRUDED_MARGIN;
      const dy = row * cellH + EXTRUDED_MARGIN;
      // Tuile, puis ses bords et ses coins recopiés d'un pixel vers l'extérieur.
      ctx.drawImage(source, sx, sy, w, h, dx, dy, w, h);
      ctx.drawImage(source, sx, sy, w, 1, dx, dy - 1, w, 1);
      ctx.drawImage(source, sx, sy + h - 1, w, 1, dx, dy + h, w, 1);
      ctx.drawImage(source, sx, sy, 1, h, dx - 1, dy, 1, h);
      ctx.drawImage(source, sx + w - 1, sy, 1, h, dx + w, dy, 1, h);
      ctx.drawImage(source, sx, sy, 1, 1, dx - 1, dy - 1, 1, 1);
      ctx.drawImage(source, sx + w - 1, sy, 1, 1, dx + w, dy - 1, 1, 1);
      ctx.drawImage(source, sx, sy + h - 1, 1, 1, dx - 1, dy + h, 1, 1);
      ctx.drawImage(source, sx + w - 1, sy + h - 1, 1, 1, dx + w, dy + h, 1, 1);
      canvasTexture.add(index++, 0, dx, dy, w, h);
    }
  }
  canvasTexture.refresh();
  extrudedFrom.set(dstKey, source);
  return dstKey;
}

/**
 * `map.addTilesetImage` sur une version extrudée de la texture du tileset
 * (mêmes dimensions de tuile, marge et espacement que dans le TMJ).
 */
export function addExtrudedTilesetImage(
  scene: Phaser.Scene,
  map: Phaser.Tilemaps.Tilemap,
  tileset: Phaser.Tilemaps.Tileset,
  textureKey: string,
): Phaser.Tilemaps.Tileset | null {
  if (!scene.textures.exists(textureKey)) return map.addTilesetImage(tileset.name, textureKey);
  const key = extrudeTileTexture(
    scene, textureKey, tileset.tileWidth, tileset.tileHeight, tileset.tileMargin, tileset.tileSpacing,
  );
  return map.addTilesetImage(
    tileset.name, key, tileset.tileWidth, tileset.tileHeight, EXTRUDED_MARGIN, EXTRUDED_SPACING, tileset.firstgid,
  );
}
